import { useEffect, useState, useRef } from "react";
import { Plus, Trash2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useApp } from "../context/AppContext";
import { PageHeader, TableShell, EmptyRow } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const emptyUser = { name: "", email: "", password: "", role: "employee" };

export default function Settings() {
  const { user, t } = useApp();
  const [settings, setSettings] = useState({ company_name: "", address: "", phone: "", email: "", tax_id: "", review_link: "", currency: "USD" });
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyUser);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadLogo = () =>
    api.get("/settings/logo", { responseType: "blob" })
      .then((r) => setLogoUrl(URL.createObjectURL(r.data)))
      .catch(() => setLogoUrl(null));

  const loadUsers = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    loadUsers();
    loadLogo();
  }, []);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/settings/logo", fd);
      toast.success(t("saved"));
      loadLogo();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    try { await api.delete("/settings/logo"); setLogoUrl(null); toast.success(t("deleted")); }
    catch (err) { toast.error(apiError(err)); }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try { await api.put("/settings", settings); toast.success(t("saved")); }
    catch (err) { toast.error(apiError(err)); }
  };

  const saveUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success(t("saved")); setOpen(false); setForm(emptyUser); loadUsers();
    } catch (err) { toast.error(apiError(err)); }
  };

  const removeUser = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/users/${id}`); toast.success(t("deleted")); loadUsers(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const set = (k) => (e) => setSettings({ ...settings, [k]: e.target.value });

  return (
    <div className="space-y-8">
      <PageHeader title={t("settings")} />
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm max-w-2xl">
        <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">{t("logo")}</h2>
        <p className="text-sm text-slate-500 mb-4">{t("logoHint")}</p>
        <div className="flex items-center gap-6">
          <div className="h-20 w-40 border border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden">
            {logoUrl ? (
              <img data-testid="logo-preview" src={logoUrl} alt="Company logo" className="max-h-full max-w-full object-contain" />
            ) : (
              <ImageIcon className="text-slate-300" size={28} />
            )}
          </div>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              data-testid="logo-file-input" onChange={uploadLogo} />
            <Button data-testid="upload-logo-button" type="button" disabled={uploading}
              className="h-11 bg-sky-600 hover:bg-sky-700 text-white font-semibold" onClick={() => fileRef.current?.click()}>
              {uploading ? "..." : t("uploadLogo")}
            </Button>
            {logoUrl && (
              <Button data-testid="remove-logo-button" type="button" variant="outline" className="h-11 text-red-500 hover:text-red-700" onClick={removeLogo}>
                {t("removeLogo")}
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm max-w-2xl">
        <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">{t("companyInfo")}</h2>
        <form onSubmit={saveSettings} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>{t("companyName")}</Label>
              <Input data-testid="settings-company-name" value={settings.company_name} onChange={set("company_name")} className="h-11" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>{t("address")}</Label>
              <Input data-testid="settings-address" value={settings.address} onChange={set("address")} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("phone")}</Label>
              <Input data-testid="settings-phone" value={settings.phone} onChange={set("phone")} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("email")}</Label>
              <Input data-testid="settings-email" value={settings.email} onChange={set("email")} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("taxId")}</Label>
              <Input data-testid="settings-tax-id" value={settings.tax_id || ""} onChange={set("tax_id")} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("currency")} (PDF)</Label>
              <Select value={settings.currency} onValueChange={(v) => setSettings({ ...settings, currency: v })}>
                <SelectTrigger data-testid="settings-currency-select" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">US$ (USD)</SelectItem>
                  <SelectItem value="BRL">R$ (BRL)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>{t("reviewLink")}</Label>
              <Input data-testid="settings-review-link" value={settings.review_link || ""} onChange={set("review_link")}
                className="h-11" placeholder="https://g.page/r/..." />
              <p className="text-xs text-slate-400">{t("reviewLinkHint")}</p>
            </div>
          </div>
          <Button data-testid="settings-save-button" type="submit" className="h-11 bg-sky-600 hover:bg-sky-700 text-white font-semibold">{t("save")}</Button>
        </form>
      </div>
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-semibold text-slate-900">{t("users")}</h2>
          <Button data-testid="add-user-button" onClick={() => { setForm(emptyUser); setOpen(true); }}
            className="h-11 bg-sky-600 hover:bg-sky-700 text-white font-semibold">
            <Plus size={18} className="mr-2" /> {t("addUser")}
          </Button>
        </div>
        <TableShell headers={[t("name"), t("email"), t("role"), t("actions")]}>
          {users.length === 0 && <EmptyRow colSpan={4} />}
          {users.map((u) => (
            <tr key={u.id} className="hover:bg-slate-50">
              <td className="px-4 py-3 font-semibold text-slate-900">{u.name}</td>
              <td className="px-4 py-3">{u.email}</td>
              <td className="px-4 py-3">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide ${u.role === "admin" ? "bg-slate-900 text-white" : "bg-sky-100 text-sky-700"}`}>
                  {t(u.role)}
                </span>
              </td>
              <td className="px-4 py-3">
                {u.id !== user?.id && (
                  <Button data-testid={`delete-user-${u.id}`} variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => removeUser(u.id)}>
                    <Trash2 size={15} />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </TableShell>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{t("addUser")}</DialogTitle></DialogHeader>
          <form onSubmit={saveUser} className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("name")} *</Label>
              <Input data-testid="user-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("email")} *</Label>
              <Input data-testid="user-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("password")} *</Label>
              <Input data-testid="user-password-input" required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("role")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">{t("employee")}</SelectItem>
                  <SelectItem value="admin">{t("admin")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" className="h-11" onClick={() => setOpen(false)}>{t("cancel")}</Button>
              <Button data-testid="user-save-button" type="submit" className="h-11 bg-sky-600 hover:bg-sky-700 text-white">{t("save")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
